import { GetHelixSearchSessionRes } from '../../api/person/models/getHelixSearchSession/GetHelixSearchSessionRes'
import Modal from '../Modal'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck } from '@fortawesome/free-solid-svg-icons'
import { useEffect } from 'react'
import HelixSearchInsights from './HelixSearchInsights'

interface Props {
    session: GetHelixSearchSessionRes
    onClose: () => void
    showResults: boolean
}

export default function HelixSearchProgressModal({
    session,
    onClose,
    showResults,
}: Props) {
    const summary = session.sessionSummary
    const hasRoleContext = !!summary.roleId

    const allSteps = [
        {
            id: 'HelixBooleanSearchEval',
            label: 'Finding suitable candidates...',
        },
        { id: 'HelixRelationshipEval', label: 'Generating network...' },
        { id: 'HelixRelationshipScoreEval', label: 'Scoring relationships...' },
        {
            id: 'HelixShortlistEval',
            label: "Checking candidates you've already reviewed...",
        },
        { id: 'COMPLETE', label: 'Preparing results...' },
    ]

    const steps = (
        hasRoleContext
            ? allSteps
            : allSteps.filter((s) => s.id !== 'HelixShortlistEval')
    ).map((step, index) => ({ ...step, index }))

    const currentStage =
        summary.status === 'COMPLETE' ? 'COMPLETE' : summary.pipelineStage

    const displayStepIndex =
        steps.find((s) => s.id === currentStage)?.index ?? 0

    useEffect(() => {
        if (showResults) {
            onClose()
        }
    }, [showResults, onClose])

    return (
        <Modal
            onClose={onClose}
            title="Search Progress"
            size="medium"
            allowUserToClose={false}
        >
            <div className="space-y-4">
                <div className="flex flex-col">
                    {steps.map((step) => {
                        const isCurrent = step.index === displayStepIndex
                        const isCompleted = step.index < displayStepIndex

                        return (
                            <div key={step.id} className="flex items-center">
                                <div className="flex flex-col items-center mr-4">
                                    {step.index !== 0 && (
                                        <div className="w-[2px] h-2 bg-gray-300" />
                                    )}
                                    <div
                                        className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                            isCompleted
                                                ? 'bg-green-500 text-white'
                                                : isCurrent
                                                ? 'bg-blue-500 text-white animate-pulse'
                                                : 'bg-gray-300 text-gray-600'
                                        }`}
                                    >
                                        {isCompleted ? (
                                            <FontAwesomeIcon icon={faCheck} />
                                        ) : isCurrent ? (
                                            <div className="w-3 h-3 bg-white rounded-full" />
                                        ) : (
                                            <div className="w-2 h-2 rounded-full bg-white" />
                                        )}
                                    </div>
                                    {step.index !== steps.length - 1 && (
                                        <div className="w-[2px] h-2 bg-gray-300" />
                                    )}
                                </div>
                                <div>
                                    <span className="text-sm font-medium">
                                        {step.label}
                                    </span>
                                </div>
                            </div>
                        )
                    })}
                </div>
                <div className="text-sm">
                    <HelixSearchInsights
                        compact={true}
                        total={summary?.totalUniqueCandidatePoolCount ?? 0}
                        matchesCriteria={
                            summary?.booleanSearchEvalPersonCount ?? 0
                        }
                        inNetwork={summary?.relationshipEvalPersonCount ?? 0}
                        matchesBoth={summary?.fullyQualifiedPersonCount ?? 0}
                    />
                </div>
            </div>
        </Modal>
    )
}
